import { FC, useContext } from "react";
import { GridActionsCellItem, GridColDef, GridRowParams } from "@mui/x-data-grid-pro";
import DataTable, { CellSize, DataTableProps } from "../table";
import { Typography, Link, Chip, Stack } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { DbAssetMarkerInfos } from "../../../Core/Api/Types/db-asset-marker-infos";
import { PreviewMarker } from "../../Cards/QCMarkerCard/qc-marker-card";
import { DbMarkerCategory } from "../../../Core/Api/Types/db-marker-category";
import { useDispatch } from "react-redux";
import { GoToFrame } from "../../../Redux/Reducers/redux-slice-react-player";
import { DbMarkerSeverity } from "../../../Core/Api/Types/db-marker-severity";
import { DbMarkerLevel } from "../../../Core/Api/Types/db-marker-level";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import { DbMarkerStatus } from "../../../Core/Api/Types/db-marker-status";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import BuildIcon from "@mui/icons-material/Build";
import { ModalContext } from "../../Modals";
import { ModalMarkerActionProps } from "../../Modals/ModalMarkers/modal-marker-action";
import ModalMarkerAction from "../../Modals/ModalMarkers/modal-marker-action";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import { useTranslation } from "react-i18next";
import TableUuids from "../table-uuids";

interface MarkersTableProps extends Omit<DataTableProps, "columns" | "rows"> {
    objectId: number;
    markers: DbAssetMarkerInfos[];
    playerSeekEnable: boolean;
    onMarkerAdd?: (marker: DbAssetMarkerInfos) => void;
    onMarkerEdit?: (marker: DbAssetMarkerInfos) => void;
    onMarkerDelete?: (marker: DbAssetMarkerInfos) => void;
    onMarkerRowClick?: (marker: DbAssetMarkerInfos) => void;
    onMarkerRevision?: (markerId: number, newStatus: any, comment?: string) => void;
}

const MarkersTable: FC<MarkersTableProps> = (props) => {
    const { t } = useTranslation();
    const {
        objectId,
        markers,
        pageSize = 20,
        playerSeekEnable,
        onMarkerAdd,
        onMarkerEdit,
        onMarkerDelete,
        onMarkerRowClick,
        onMarkerRevision,
        ...otherProps
    } = props;
    const dispatch = useDispatch();

    const thumbWidth = 100;
    const thumbHeight = 56;
    const margin = 16;

    // Modal
    const modalContext = useContext(ModalContext);
    const { openModal } = { ...modalContext };

    // Acls
    const aclAddEdit = UserHasAcl(AclTypes.aclMarkerCreateOrUpdate);
    const aclDelete = UserHasAcl(AclTypes.aclMarkerDelete);
    const aclBypassOrAskToFixIssue = UserHasAcl(AclTypes.aclMarkerBypassOrAskToFixIssue);
    const aclFixIssue = UserHasAcl(AclTypes.aclMarkerFixIssue);

    const _renderThumb = (thumbData: any, timecode: string | null, frame: number | null) => {
        return (
            <Stack alignItems="center">
                {thumbData && (
                    <PreviewMarker
                        img={{ src: "data:image/jpeg;base64," + thumbData.$value! }}
                        width={thumbWidth}
                        height={thumbHeight}
                        flexShrink={0}
                        mt={1}
                        mb={1}
                    />
                )}
                {!timecode && (
                    <Typography component="span" color="text.primary">
                        {t("table:markersTable.body.noTimecode")}
                    </Typography>
                )}
                {timecode && frame && (
                    <Link
                        color="primary"
                        underline="hover"
                        onClick={(e) => {
                            e.stopPropagation();
                            playerSeekEnable && dispatch(GoToFrame(frame));
                        }}
                        mb={1}
                    >
                        {timecode}
                    </Link>
                )}
            </Stack>
        );
    };

    const _renderCategory = (category: DbMarkerCategory) => {
        let text = "";
        let color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" = "default";

        switch (category) {
            case DbMarkerCategory.BatonAutoQc:
                text = "Interra Baton";
                color = "info";
                break;
            case DbMarkerCategory.Human:
                text = t("common:human");
                color = "primary";
                break;
            case DbMarkerCategory.SceneChange:
                text = t("chip:label.sceneChange");
                color = "default";
                break;
            case DbMarkerCategory.ThumbPgm:
                text = t("chip:label.programThumbnail");
                break;
            case DbMarkerCategory.ThumbRef:
                text = t("chip:label.programRefThumbnail");
                break;
            case DbMarkerCategory.DubCardImageExtraction:
                text = t("chip:label.imageExtraction");
                color = "secondary";
                break;
        }

        return <Chip label={text} color={color} size="small" />;
    };

    const _renderSeverity = (category: DbMarkerSeverity, level: DbMarkerLevel) => {
        let text = "";
        let color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" = "default";

        switch (category) {
            case DbMarkerSeverity.INFO:
                text = `${t("common:upperCaseInfo")} / ${level}`;
                break;
            case DbMarkerSeverity.WARNING:
                text = `${t("common:warning")} / ${level}`;
                color = "warning";
                break;
            case DbMarkerSeverity.ERROR:
                text = `${t("common:upperCaseError")} / ${level}`;
                color = "error";
                break;
        }

        return <Chip label={text} color={color} size="small" />;
    };

    // Cols def
    const cols: GridColDef[] = [
        {
            field: "thumbnailIn",
            headerName: t("common:in"),
            hideable: true,
            flex: thumbWidth + margin,
            minWidth: thumbWidth + margin,
            renderCell: (data: any) =>
                _renderThumb(data.value, data?.row?.timecodeInFile ?? data?.row?.timecodeIn, data?.row?.frameIn),
        },
        {
            field: "thumbnailOut",
            headerName: t("common:out"),
            hideable: true,
            flex: thumbWidth + margin,
            minWidth: thumbWidth + margin,
            renderCell: (data: any) =>
                _renderThumb(data.value, data?.row?.timecodeOutFile ?? data?.row?.timecodeOut, data?.row?.frameOut),
        },
        {
            field: "severity",
            headerName: `${t("common:severity")} / ${t("common:level")}`,
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: any) => _renderSeverity(data.value, data?.row?.level),
        },
        {
            field: "category",
            headerName: t("common:category"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: any) => _renderCategory(data.value),
        },
        {
            field: "familyInfos",
            headerName: t("common:family"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: any) => (
                <Typography component="span" fontWeight={500} color="text.primary" pt={0.5} pb={0.5}>
                    {data.value !== null ? data.value.name : "-"}
                </Typography>
            ),
        },
        {
            field: "comment",
            headerName: t("common:comment"),
            flex: CellSize.xlarge,
            minWidth: CellSize.xlarge,
            renderCell: (data: any) => (
                <Stack spacing={0.5} mt={0.5} mb={0.5} alignItems={"flex-start"}>
                    <Chip label={data?.row?.typeInfos !== null ? data?.row?.typeInfos.name : "-"} />
                    <Typography component="span" color="text.tier" pt={0.5} pb={0.5}>
                        {data.value}
                    </Typography>
                </Stack>
            ),
        },
        {
            field: "commentReviewer",
            headerName: t("table:headerName.reviewerComment"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: any) => (
                <Typography component="span" color="text.tier" pt={0.5} pb={0.5}>
                    {data.value}
                </Typography>
            ),
        },
        {
            field: "revision",
            headerName: t("common:revision"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: any) => {
                let array: any[] = [];

                if (data?.row?.commentReviewer !== null) {
                    array.push(<ChatOutlinedIcon sx={{ m: 0.5 }} />);
                }

                if (
                    data?.row?.status === DbMarkerStatus.Bypassed ||
                    data?.row?.status === DbMarkerStatus.TobeFixed ||
                    data?.row?.status === DbMarkerStatus.Fixed
                )
                    array.push(
                        data?.row?.status === DbMarkerStatus.Bypassed ? (
                            <ThumbUpIcon sx={{ m: 0.5 }} />
                        ) : data?.row?.status === DbMarkerStatus.TobeFixed ? (
                            <BuildIcon sx={{ m: 0.5 }} />
                        ) : (
                            <CheckCircleOutlinedIcon sx={{ m: 0.5 }} />
                        )
                    );

                return array;
            },
        },
        {
            field: "actions",
            type: "actions",
            flex: CellSize.actionsXsmall,
            minWidth: CellSize.actionsXsmall,
            align: "right",
            getActions: (params: GridRowParams) => {
                if (!params || !params.row) return [];
                let array: any[] = [];
                if (
                    aclBypassOrAskToFixIssue &&
                    params?.row?.status !== DbMarkerStatus.Bypassed &&
                    params?.row?.status !== DbMarkerStatus.Fixed &&
                    params?.row?.status !== DbMarkerStatus.TobeFixed
                )
                    array.push(
                        <GridActionsCellItem
                            icon={<ThumbUpIcon sx={{ width: 20, height: 20 }} />}
                            label={t("common:bypass")}
                            onClick={() =>
                                openModal &&
                                openModal<ModalMarkerActionProps>(ModalMarkerAction, {
                                    context: "Bypass",
                                    markerId: params?.row?.id,
                                    onConfirm: (markerId, newStatus, comment) => {
                                        onMarkerRevision && onMarkerRevision(markerId, newStatus, comment);
                                    },
                                    closeModal: () => false,
                                })
                            }
                            showInMenu
                        />
                    );

                if (
                    aclBypassOrAskToFixIssue &&
                    params?.row?.status !== DbMarkerStatus.TobeFixed &&
                    params?.row?.status !== DbMarkerStatus.Fixed &&
                    params?.row?.status !== DbMarkerStatus.Bypassed
                )
                    array.push(
                        <GridActionsCellItem
                            icon={<BuildIcon sx={{ width: 20, height: 20 }} />}
                            label={t("table:cellLabel.fixIt")}
                            onClick={() =>
                                openModal &&
                                openModal<ModalMarkerActionProps>(ModalMarkerAction, {
                                    context: "FixIt",
                                    markerId: params?.row?.id,
                                    onConfirm: (markerId, newStatus, comment) => {
                                        onMarkerRevision && onMarkerRevision(markerId, newStatus, comment);
                                    },
                                    closeModal: () => false,
                                })
                            }
                            showInMenu
                        />
                    );

                if (aclFixIssue && params?.row?.status === DbMarkerStatus.TobeFixed)
                    array.push(
                        <GridActionsCellItem
                            icon={<CheckCircleOutlinedIcon sx={{ width: 20, height: 20 }} />}
                            label={t("modal:title.issueFixed")}
                            onClick={() =>
                                openModal &&
                                openModal<ModalMarkerActionProps>(ModalMarkerAction, {
                                    context: "Fixed",
                                    markerId: params?.row?.id,
                                    onConfirm: (markerId, newStatus, comment) => {
                                        onMarkerRevision && onMarkerRevision(markerId, newStatus, comment);
                                    },
                                    closeModal: () => false,
                                })
                            }
                            showInMenu
                        />
                    );

                array.push(
                    <GridActionsCellItem
                        icon={<ChatOutlinedIcon sx={{ width: 20, height: 20 }} />}
                        label={t("common:comment")}
                        onClick={() =>
                            openModal &&
                            openModal<ModalMarkerActionProps>(ModalMarkerAction, {
                                context: "Comment",
                                markerId: params?.row?.id,
                                onConfirm: (markerId, newStatus, comment) => {
                                    onMarkerRevision && onMarkerRevision(markerId, newStatus, comment);
                                },
                                closeModal: () => false,
                            })
                        }
                        showInMenu
                    />
                );

                array.push(
                    <GridActionsCellItem
                        icon={<DeleteIcon sx={{ width: 20, height: 20 }} />}
                        label={t("common:delete")}
                        color="error"
                        onClick={() => onMarkerDelete && onMarkerDelete(params.row)}
                        disabled={
                            (params?.row?.category !== DbMarkerCategory.Human &&
                                params?.row?.category !== DbMarkerCategory.DubCardImageExtraction) ||
                            !aclDelete
                        }
                        showInMenu
                    />
                );

                return array;
            },
        },
    ];

    return (
        <>
            <DataTable
                {...otherProps}
                tableKey={TableUuids.MARKERS_TABLE}
                rows={markers}
                columns={cols}
                columnHeaderHeight={48}
                rowHeight={56}
                getRowHeight={() => "auto"}
                pageSize={pageSize}
                disableRowSelectionOnClick
                disableMultipleRowSelection
                pagination={true}
                onRowClick={(data) => aclAddEdit && onMarkerRowClick && onMarkerRowClick(data.row)}
                initialColumnVisibility={{
                    revision: markers.filter((marker) => marker.commentReviewer !== null).length > 0,
                    commentReviewer: markers.filter((marker) => marker.commentReviewer !== null).length > 0,
                }}
            />
        </>
    );
};

export default MarkersTable;
